import "@babylonjs/loaders/glTF";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { Scene } from "@babylonjs/core/scene";
import type { AssetContainer } from "@babylonjs/core/assetContainer";
import needleUrl from "../assets/ships/needle.glb";
import deltaUrl from "../assets/ships/delta.glb";
import bruteUrl from "../assets/ships/brute.glb";
import strikerUrl from "../assets/ships/striker.glb";
import forkUrl from "../assets/ships/fork.glb";
import finbackUrl from "../assets/ships/finback.glb";
import swiftUrl from "../assets/ships/swift.glb";

/**
 * The craft models, one GLB per design (28 Sep 2026: a concept image, a
 * Hunyuan high poly, a Hunyuan Studio low poly, the high's colour baked onto
 * it, then its normal and metallic-roughness maps; sources and bake logs in
 * VectorDrift-ShipConcepts). Each is a low-poly hull of 1.7k–3.3k triangles:
 * a 2K WebP colour atlas, 1K normal and metallic-roughness maps, nose along +Z.
 * A roster ship names its design in `ShipSpec.model`.
 */
export type ShipModelId = "needle" | "delta" | "brute" | "striker" | "fork" | "finback" | "swift";

const MODEL_URLS: Record<ShipModelId, string> = {
  needle: needleUrl,
  delta: deltaUrl,
  brute: bruteUrl,
  striker: strikerUrl,
  fork: forkUrl,
  finback: finbackUrl,
  swift: swiftUrl,
};

/** Every design. */
export const SHIP_MODEL_IDS = Object.keys(MODEL_URLS) as ShipModelId[];

/** How strongly a hull shows its own colours unlit, so the livery still reads
 * on the dark tracks (the colour is baked, with no lighting in it). Kept low:
 * the paint and metal also pick up the course's sky (world/shipReflections). */
const SELF_LIT = 0.12;

const containers = new WeakMap<Scene, Map<ShipModelId, AssetContainer | null>>();
const loading = new WeakMap<Scene, Map<ShipModelId, Promise<void>>>();

/** Load these designs into a scene (each once). Safe to call repeatedly. */
export function preloadShipModels(scene: Scene, ids: Iterable<ShipModelId>): Promise<void> {
  let pending = loading.get(scene);
  if (!pending) loading.set(scene, (pending = new Map()));
  let loaded = containers.get(scene);
  if (!loaded) containers.set(scene, (loaded = new Map()));
  const all: Promise<void>[] = [];
  for (const id of ids) {
    let p = pending.get(id);
    if (!p) {
      const got = loaded;
      p = SceneLoader.LoadAssetContainerAsync("", MODEL_URLS[id], scene)
        .then((container) => {
          for (const mat of container.materials) {
            if (!(mat instanceof PBRMaterial)) continue;
            mat.emissiveTexture = mat.albedoTexture;
            mat.emissiveColor = new Color3(SELF_LIT, SELF_LIT, SELF_LIT);
          }
          got.set(id, container);
        })
        .catch((e) => {
          console.warn(`ship model ${id} failed to load`, e);
          got.set(id, null);
        });
      pending.set(id, p);
    }
    all.push(p);
  }
  return Promise.all(all).then(() => undefined);
}

export function shipModelReady(scene: Scene, id: ShipModelId): boolean {
  return !!containers.get(scene)?.get(id);
}

let serial = 0;

/**
 * An instance of design `id`, normalised so its longest dimension is
 * `targetLength`, centred on the origin, nose along +Z. Returns a parent
 * TransformNode (rotate/position it however you like) or null if the design
 * isn't loaded yet.
 */
export function buildShipModel(scene: Scene, id: ShipModelId, targetLength: number): TransformNode | null {
  const container = containers.get(scene)?.get(id);
  if (!container) return null;

  // The hulls aren't tinted, so instances share their materials.
  const entries = container.instantiateModelsToScene((n) => n, false);
  const root = entries.rootNodes[0] as TransformNode | undefined;
  if (!root) return null;
  root.setEnabled(true);

  // Normalise size + centre.
  root.computeWorldMatrix(true);
  let b = root.getHierarchyBoundingVectors(true);
  const longest = Math.max(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) || 1;
  root.scaling.scaleInPlace(targetLength / longest);
  root.computeWorldMatrix(true);
  b = root.getHierarchyBoundingVectors(true);
  const cx = (b.min.x + b.max.x) / 2;
  const cy = (b.min.y + b.max.y) / 2;
  const cz = (b.min.z + b.max.z) / 2;

  const wrap = new TransformNode(`shipModel_${id}_${serial++}`, scene);
  root.parent = wrap;
  root.position.set(-cx, -cy, -cz);
  return wrap;
}
